// src/components/block/liveStreamManagement/HlsAudioPanel.tsx
//
// Deux modes :
//
// MODE 1 (existant) — mediaStream fourni, pas d'audioSourceRegistry.
//   Crée son propre AudioContext, createMediaStreamSource, splitter,
//   analyseurs, GainNode, ctx.destination. Comportement inchangé.
//
// MODE 2 (nouveau) — audioSourceRegistry + audioSourceId fournis.
//   N'crée aucun AudioContext. Emprunte les analyseurs et les nœuds
//   déjà créés par AudioSourceRegistry.borrowFromStream() dans
//   useMonitorStream. Contrôle le gain via setGain/setMuted/setMonitorGain.
//
// Fix : dans le MODE 2, la taille des buffers dataL/dataR est lue depuis
//       analyserL.fftSize au moment du premier tick — évite le mismatch
//       entre fftSize=2048 (registry) et Float32Array(256) (panel).

import React, { useEffect, useRef, useState } from 'react';
import {
  createMeter,
  MeterType,
  MeterInstance,
} from '../../../dataTypes/liveStreamManagment/audioPipeline/audioMeter';
import { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import { LUFS_TARGET, VuMeterMode, getVuMeterMode, useVuMeterMode, vuGradient, vuSegments } from './vuMeterZones';
import { MOMENTARY_WINDOW_S, lufsFromMeanSquares, meanSquareTail } from '../../../dataTypes/liveStreamManagment/audioPipeline/loudness';

// ── Échelle visuelle logarithmique ────────────────────────────────────────────

const MIN_DB   = -64;
const MAX_DB   =   6;
const TOTAL_DB = MAX_DB - MIN_DB;

function dbToFrac(db: number): number {
  const n = Math.max(0, Math.min(1, (db - MIN_DB) / TOTAL_DB));
  return n * n;
}

// Zones d'une table de mixage, fixes sur l'échelle (vuMeterZones, 2026-10-23) :
// vert ≤ −18 dB, jaune ≤ −6, orange ≤ 0, rouge au-delà ; segments façon LED.
// En mode LUFS, mêmes graduations lues en LUFS, zones autour de la cible (−14).
const METER_GRADIENT = { peak: vuGradient('to top', db => dbToFrac(db) * 100, 'peak'), lufs: vuGradient('to top', db => dbToFrac(db) * 100, 'lufs') };
const METER_SEGMENTS = vuSegments('to top', 4);

/** Mode LUFS : intervalle de recalcul de la loudness (20 par seconde). */
const LUFS_REFRESH_MS = 50;

/**
 * Loudness Momentary (LUFS) d'une entrée du registry, gain `gain` rejoué, ou null si
 * l'entrée n'a pas de branche pondérée K (navigateur sans IIRFilterNode). `buf` garde
 * les tampons d'un appel à l'autre.
 */
function readLufs(registry: AudioSourceRegistry, id: string, gain: number, buf: { L?: Float32Array; R?: Float32Array }): number | null {
  const loud = registry.getLoudnessAnalysers(id);
  if (!loud) return null;
  if (!buf.L || buf.L.length !== loud.analyserL.fftSize) {
    buf.L = new Float32Array(loud.analyserL.fftSize);
    buf.R = new Float32Array(loud.analyserR.fftSize);
  }
  loud.analyserL.getFloatTimeDomainData(buf.L);
  loud.analyserR.getFloatTimeDomainData(buf.R!);
  const n    = Math.round(MOMENTARY_WINDOW_S * loud.analyserL.context.sampleRate);
  const lufs = lufsFromMeanSquares([meanSquareTail(buf.L, n), meanSquareTail(buf.R!, n)]);
  if (gain <= 0) return MIN_DB;
  return Math.max(MIN_DB, Math.min(MAX_DB, lufs + 20 * Math.log10(gain)));
}

const DB_MARKS = [6, 0, -6, -12, -18, -24, -36, -48, -60];
// Chiffres de la colonne de gauche : tous sauf −48, trop près de −60 en bas de voie (les
// deux se chevauchaient). Son trait reste tracé. 2026-10-18.
const DB_LABELLED = DB_MARKS.filter(db => db !== -48);

const LABEL_COL_W = 26;
const TRACK_W     =  6;
const HANDLE_OVF  =  8;
const HANDLE_H    =  2;
// Capuchon (2026-10-18) : aussi large que la colonne du fader (TRACK_W + 2 × HANDLE_OVF),
// donc rien ne bouge dans la mise en page, et assez haut pour se saisir au doigt.
const CAP_H       = 28;
const CAP_W       = TRACK_W + HANDLE_OVF * 2;

// ── Helpers slider log ────────────────────────────────────────────────────────

function sliderToDB(val: number): number {
  if (val <= 0) return -Infinity;
  return MIN_DB + Math.sqrt(val / 100) * TOTAL_DB;
}

function dbToSlider(db: number): number {
  return Math.max(0, Math.min(100, dbToFrac(db) * 100));
}

function fmtDB(db: number): string {
  if (!isFinite(db)) return '-∞ dB';
  return (db >= 0 ? '+' : '') + db.toFixed(1) + ' dB';
}

function parseGainInput(s: string): number | null {
  const clean = s.replace(/\s*dB$/i, '').trim();
  if (clean === '-∞' || clean === '-inf' || clean === '') return 0;
  const db = parseFloat(clean);
  if (isNaN(db)) return null;
  return dbToSlider(Math.max(MIN_DB, Math.min(MAX_DB, db)));
}

// ── Valeur slider correspondant exactement à 0 dB — exportée pour le Sheet ──
export const SLIDER_0DB = dbToSlider(0);
const SNAP_RANGE = 1.5;

/** Valeur de slider (0–100) ↔ gain linéaire (1 = 0 dB). */
export const sliderToGain = (v: number): number => {
  const db = sliderToDB(v);
  return isFinite(db) ? Math.pow(10, db / 20) : 0;
};
export const gainToSlider = (g: number): number => (g <= 0 ? 0 : dbToSlider(20 * Math.log10(g)));

// ── Comptage des panneaux branchés sur une même entrée de registry ───────────
//
// En MODE 2, plusieurs panneaux partagent la MÊME entrée : le tableau de bord,
// la vue Retour de la régie et la vue Intercom pointent tous sur
// 'audio-hls-monitor-retour'. Couper le retour au démontage de l'un d'eux
// rendait muets tous les autres (ex. : multiview détaché en fenêtre externe).
// On ne coupe donc qu'au démontage du dernier emprunteur.

const monitorRefCounts = new WeakMap<AudioSourceRegistry, Map<string, number>>();

function acquireMonitor(registry: AudioSourceRegistry, id: string): void {
  let counts = monitorRefCounts.get(registry);
  if (!counts) { counts = new Map(); monitorRefCounts.set(registry, counts); }
  counts.set(id, (counts.get(id) ?? 0) + 1);
}

/** @returns true si c'était le dernier panneau branché — le retour peut être coupé. */
function releaseMonitor(registry: AudioSourceRegistry, id: string): boolean {
  const counts = monitorRefCounts.get(registry);
  if (!counts) return true;
  const next = (counts.get(id) ?? 1) - 1;
  if (next > 0) { counts.set(id, next); return false; }
  counts.delete(id);
  return true;
}

// ── Canal VU continu ─────────────────────────────────────────────────────────

const VuBar: React.FC<{ level: number; muted: boolean; label: string; mode: VuMeterMode }> = ({
  level, muted, label, mode,
}) => {
  const fillFrac = muted ? 0 : dbToFrac(level);
  const clipTop  = ((1 - fillFrac) * 100).toFixed(2);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 3, flex: '0 0 9px',
      position: 'relative', zIndex: 1,
    }}>
      <span style={{ fontSize: 7, fontFamily: 'monospace', color: 'rgba(255,255,255,0.30)', lineHeight: 1 }}>
        {label}
      </span>
      <div style={{
        position: 'relative', width: 9, flex: '1 1 0', minHeight: 0,
        borderRadius: 3, overflow: 'hidden', background: '#000',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: METER_GRADIENT[mode], opacity: 0.13 }} />
        <div style={{
          position:   'absolute',
          inset:      0,
          background: METER_GRADIENT[mode],
          clipPath:   `inset(${clipTop}% 0 0 0)`,
          transition: 'clip-path 0.04s ease',
        }} />
        <div style={{ position: 'absolute', inset: 0, background: METER_SEGMENTS, pointerEvents: 'none' }} />
        {mode === 'lufs' && <div title={`Cible ${LUFS_TARGET} LUFS`} style={{ position: 'absolute', left: 0, right: 0, bottom: `${(dbToFrac(LUFS_TARGET) * 100).toFixed(2)}%`, height: 2, background: '#ffffff', pointerEvents: 'none' }} />}
      </div>
    </div>
  );
};

// ── Icônes ────────────────────────────────────────────────────────────────────

const IconMuted = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <line x1="23" y1="9"  x2="17" y2="15"/>
    <line x1="17" y1="9"  x2="23" y2="15"/>
  </svg>
);

const IconSound = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
  </svg>
);

// ── Props ─────────────────────────────────────────────────────────────────────

interface HlsAudioPanelProps {
  // MODE 1 — stream direct (comportement original)
  mediaStream?:     MediaStream | null;
  meterType?:       MeterType;

  // MODE 2 — AudioSourceRegistry partagé (pas de nouvel AudioContext)
  audioSourceRegistry?: AudioSourceRegistry;
  audioSourceId?:       string;

  // Sync externe volume/mute (commun aux deux modes)
  externalVolume?:  number;
  externalMuted?:   boolean;
  onVolumeChange?:  (v: number) => void;
  onMutedChange?:   (m: boolean) => void;

  // Option — ne pas connecter à ctx.destination (mode micro sans retour moniteur)
  noOutput?:     boolean;
  // Appelé à chaque changement de gain (gain linéaire effectif, 0 si muté)
  onGainChange?: (linearGain: number) => void;

  // MODE 3 — voie de mixeur : le panneau ne touche à AUCUN nœud audio. Il lit le
  // niveau de `meterSourceId` (audioLevelStore) et remonte volume/mute au parent,
  // qui applique le son. Le mute est au-dessus du fader, allumé en vert, et ne
  // déplace pas le fader (le niveau est conservé, simplement ignoré).
  meterSourceId?: string;
  meterRegistry?: AudioSourceRegistry;
  // Mode 3 : le parent affiche le MUTE ailleurs (bloc Mixeur du deck : à côté du
  // nom de la voie) et le pilote par externalMuted / onMutedChange.
  hideMeterMute?: boolean;
  // Bouton posé à droite de la valeur en dB, sous le fader (bloc Mixeur du deck :
  // engrenage des effets). La valeur en dB passe alors à gauche.
  footerExtra?: React.ReactNode;
}

// ── Slider vertical style T-Bar ───────────────────────────────────────────────

interface TBarSliderProps {
  value:    number;
  onChange: (v: number) => void;
  atZero:   boolean;
}

const TBarSlider: React.FC<TBarSliderProps> = ({ value, onChange, atZero }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [trackHeight, setTrackHeight] = useState(0);
  // États visuels du capuchon (le glisser lui-même reste piloté par la ref ci-dessus).
  const [hover, setHover] = useState(false);
  const [held , setHeld ] = useState(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setTrackHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleY = (1 - value / 100) * trackHeight; // ordonnée du CENTRE du repère, alignée sur les graduations

  // À l'unité (0 dB), le repère passe en ambre : le réglage par défaut se voit de loin.
  const indexColor   = atZero ? '#fbbf24' : '#f8fafc';
  const handleBorder = atZero ? 'rgba(251,191,36,0.85)' : hover || held ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)';

  const trackHeightRef = useRef(0);
  useEffect(() => { trackHeightRef.current = trackHeight; }, [trackHeight]);

  // `fromCap` : saisi par le capuchon. On garde alors l'écart entre le doigt et le repère,
  // pour que le fader ne saute pas de quelques pixels à la prise (la piste, elle, y saute).
  const startDrag = (e: React.MouseEvent | React.TouchEvent, fromCap = false) => {
    e.preventDefault();
    dragging.current = true;
    setHeld(true);

    const startY = 'touches' in e ? (e as React.TouchEvent).touches[0].clientY : (e as React.MouseEvent).clientY;
    const rect0  = trackRef.current?.getBoundingClientRect();
    const grabOffset = fromCap && rect0 ? startY - (rect0.top + handleY) : 0;

    const updateFromClientY = (clientY: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const usableDrag = Math.max(0, trackHeightRef.current);
      if (usableDrag === 0) return;
      const y       = clientY - grabOffset - rect.top;
      const clamped = Math.max(0, Math.min(usableDrag, y));
      let   val     = (1 - clamped / usableDrag) * 100;
      if (Math.abs(val - SLIDER_0DB) < SNAP_RANGE) val = SLIDER_0DB;
      onChange(val);
    };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!dragging.current) return;
      updateFromClientY('touches' in ev ? ev.touches[0].clientY : ev.clientY);
    };
    const onUp = () => {
      dragging.current = false;
      setHeld(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      window.removeEventListener('touchmove', onMove as EventListener);
      window.removeEventListener('touchend',  onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    window.addEventListener('touchmove', onMove as EventListener, { passive: false });
    window.addEventListener('touchend',  onUp);

    updateFromClientY('touches' in e
      ? (e as React.TouchEvent).touches[0].clientY
      : (e as React.MouseEvent).clientY);
  };

  return (
    <div style={{ position: 'relative', flex: '1 1 0', minHeight: 0 }}>
      <div
        ref={trackRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{
          position:     'absolute',
          left:         '50%',
          transform:    'translateX(-50%)',
          top:          0,
          bottom:       0,
          width:        TRACK_W,
          background:   'linear-gradient(180deg, rgba(239,68,68,0.15), #1e293b 50%, rgba(34,197,94,0.15))',
          border:       '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
          cursor:       'ns-resize',
        }}
      />
      {/* Capuchon de fader (2026-10-18) : bloc en relief aussi large que la colonne, creusé
          au milieu comme un capuchon de console, stries de prise de part et d'autre d'un repère
          net (ambre à 0 dB). Il se saisit directement (avant, seule la piste de 6 px réagissait),
          s'éclaire au survol, grossit un peu tenu, et se règle au clavier une fois sélectionné. */}
      <div
        role="slider"
        tabIndex={0}
        aria-label="Fader"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value)}
        aria-valuetext={atZero ? '0 dB' : undefined}
        onMouseDown={e => startDrag(e, true)}
        onTouchStart={e => startDrag(e, true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onKeyDown={e => {
          const step = e.shiftKey ? 5 : 1;
          let next: number | null = null;
          if (e.key === 'ArrowUp'   || e.key === 'ArrowRight') next = value + step;
          if (e.key === 'ArrowDown' || e.key === 'ArrowLeft')  next = value - step;
          if (e.key === 'PageUp')   next = value + 10;
          if (e.key === 'PageDown') next = value - 10;
          if (e.key === 'Home')     next = SLIDER_0DB;   // retour à l'unité
          if (next === null) return;
          e.preventDefault();
          onChange(Math.max(0, Math.min(100, next)));
        }}
        style={{
          position:      'absolute',
          left:          `calc(50% - ${CAP_W / 2}px)`,
          top:           handleY - CAP_H / 2,
          boxSizing:     'border-box',
          width:         CAP_W,
          height:        CAP_H,
          borderRadius:  4,
          border:        `1px solid ${handleBorder}`,
          background:    'linear-gradient(180deg, #5b6474 0%, #2b3240 40%, #161b24 50%, #2b3240 60%, #48505e 100%)',
          boxShadow:     [
            'inset 0 1px 0 rgba(255,255,255,0.28)',
            'inset 0 -1px 0 rgba(0,0,0,0.55)',
            held ? '0 4px 10px rgba(0,0,0,0.75)' : '0 2px 5px rgba(0,0,0,0.65)',
            atZero ? '0 0 8px rgba(251,191,36,0.35)' : '',
          ].filter(Boolean).join(', '),
          cursor:        held ? 'grabbing' : 'grab',
          transform:     held ? 'scale(1.06)' : 'none',
          transition:    held ? 'transform 0.08s, box-shadow 0.08s' : 'top 0.04s, transform 0.08s, box-shadow 0.08s, border-color 0.12s',
          outline:       'none',
          touchAction:   'none',
          zIndex:        3,
        }}
      >
        {/* Repère : la ligne qui se lit sur la graduation */}
        <div style={{
          position:     'absolute',
          left:         2,
          right:        2,
          top:          '50%',
          marginTop:    -HANDLE_H / 2,
          height:       HANDLE_H,
          borderRadius: 1,
          background:   indexColor,
          boxShadow:    atZero ? '0 0 4px rgba(251,191,36,0.9)' : hover || held ? '0 0 4px rgba(248,250,252,0.7)' : 'none',
        }} />
        {/* Stries de prise, en relief (un trait clair, un trait sombre), au-dessus et au-dessous */}
        {[-10, -7, -4, 4, 7, 10].map(off => (
          <div key={off} style={{
            position:     'absolute',
            left:         '22%',
            right:        '22%',
            top:          `calc(50% + ${off}px)`,
            height:       1,
            background:   'rgba(255,255,255,0.22)',
            boxShadow:    '0 1px 0 rgba(0,0,0,0.55)',
          }} />
        ))}
      </div>
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const HlsAudioPanel: React.FC<HlsAudioPanelProps> = ({
  mediaStream,
  meterType = 'ppm',
  audioSourceRegistry,
  audioSourceId,
  externalVolume,
  externalMuted,
  onVolumeChange,
  onMutedChange,
  noOutput = false,
  onGainChange,
  meterSourceId,
  meterRegistry,
  hideMeterMute = false,
  footerExtra,
}) => {

  // Mode 2 si audioSourceRegistry + audioSourceId sont fournis
  const isRegistryMode = !!(audioSourceRegistry && audioSourceId);
  const isMeterMode    = !isRegistryMode && !!meterSourceId && !!meterRegistry;
  // Le MODE 1 (flux sans registry : micro de l'intercom) n'a pas de branche pondérée K :
  // il reste en dBFS crête, couleurs comprises, quel que soit le réglage de la régie.
  const regieVuMode = useVuMeterMode();
  const vuMode      = isRegistryMode || isMeterMode ? regieVuMode : 'peak';

  // ── Refs initialisées depuis les props externes dès le premier rendu ──────
  const volumeRef  = useRef(externalVolume !== undefined ? externalVolume : SLIDER_0DB);
  const isMutedRef = useRef(externalMuted  !== undefined ? externalMuted  : false);

  const [volume,   setVolume]   = useState(() => volumeRef.current);
  const [isMuted,  setIsMuted]  = useState(() => isMutedRef.current);
  const [gainText, setGainText] = useState(() => fmtDB(sliderToDB(volumeRef.current)));
  const [editing,  setEditing]  = useState(false);

  const [levelL, setLevelL] = useState<number>(MIN_DB);
  const [levelR, setLevelR] = useState<number>(MIN_DB);

  const setVolumeSync  = (v: number)  => { volumeRef.current  = v; setVolume(v);  };
  const setIsMutedSync = (m: boolean) => { isMutedRef.current = m; setIsMuted(m); };

  const isExternalUpdateRef = useRef(false);

  // MODE 1 — refs Web Audio internes
  const audioCtxRef  = useRef<AudioContext  | null>(null);
  const analyserLRef = useRef<AnalyserNode  | null>(null);
  const analyserRRef = useRef<AnalyserNode  | null>(null);
  const gainRef      = useRef<GainNode      | null>(null);
  const meterRef     = useRef<MeterInstance | null>(null);
  const rafRef       = useRef<number        | null>(null);

  // ── Resume AudioContext ───────────────────────────────────────────────────
  const resumeAudioContext = () => {
    if (isRegistryMode && audioSourceRegistry) {
      audioSourceRegistry.resume().catch(() => {});
    } else if (audioCtxRef.current) {
      audioCtxRef.current.resume().catch(() => {});
    }
  };

  // ── applyGain ─────────────────────────────────────────────────────────────
  /*
  const applyGain = (sliderVal: number, muted: boolean) => {
    const db = sliderToDB(sliderVal);
    const g  = (muted || !isFinite(db)) ? 0 : Math.pow(10, db / 20);

    if (isRegistryMode && audioSourceRegistry && audioSourceId) {
      audioSourceRegistry.setMuted(audioSourceId, muted);
      if (!muted) audioSourceRegistry.setGain(audioSourceId, g);
      audioSourceRegistry.setMonitorGain(audioSourceId, muted ? 0 : 1);
    } else {
      if (gainRef.current) gainRef.current.gain.value = g;
      if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
    }
  };
  */
  /*
  const applyGain = (sliderVal: number, muted: boolean) => {
  const db = sliderToDB(sliderVal);
  const g  = (muted || !isFinite(db)) ? 0 : Math.pow(10, db / 20);

  if (isRegistryMode && audioSourceRegistry && audioSourceId) {
    audioSourceRegistry.setGain(audioSourceId, muted ? 0 : g);
    // setMonitorGain seulement si muted change
    audioSourceRegistry.setMonitorGain(audioSourceId, muted ? 0 : 1);
  } else {
    if (gainRef.current) gainRef.current.gain.value = g;
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
  }
};
  */
  /*
  const applyGain = (sliderVal: number, muted: boolean) => {
  const db = sliderToDB(sliderVal);
  const g  = (muted || !isFinite(db)) ? 0 : Math.pow(10, db / 20);

  if (isRegistryMode && audioSourceRegistry && audioSourceId) {
    audioSourceRegistry.setGain(audioSourceId, 1);         // gainNode toujours à 1 — VU-mètre voit le signal plein
    audioSourceRegistry.setMonitorGain(audioSourceId, muted ? 0 : g); // monitorGainNode contrôle le son
  } else {
    if (gainRef.current) gainRef.current.gain.value = g;
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
  }
};
  */
  
  const applyGain = (sliderVal: number, muted: boolean) => {
  const db = sliderToDB(sliderVal);
  const g  = (muted || !isFinite(db)) ? 0 : Math.pow(10, db / 20);

  if (isMeterMode) {
    // Le gain remonté est celui du fader, même voie coupée : le mute est un état à part.
    onGainChange?.(isFinite(db) ? Math.pow(10, db / 20) : 0);
    return;
  }

  if (isRegistryMode && audioSourceRegistry && audioSourceId) {
    audioSourceRegistry.setGain(audioSourceId, g);          // gainNode — avant analyseurs → VU-mètre suit
    audioSourceRegistry.setMonitorGain(audioSourceId, muted ? 0 : 1); // sortie son — fixe à 1
  } else {
    if (gainRef.current) gainRef.current.gain.value = g;
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
  }
  onGainChange?.(g);
};
  
  
  // ── Sync externalVolume ───────────────────────────────────────────────────
  useEffect(() => {
    if (externalVolume === undefined) return;
    if (externalVolume === volumeRef.current) return;
    isExternalUpdateRef.current = true;
    setVolumeSync(externalVolume);
    if (!editing) setGainText(fmtDB(sliderToDB(externalVolume)));
    applyGain(externalVolume, isMutedRef.current);
    isExternalUpdateRef.current = false;
  }, [externalVolume]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync externalMuted ────────────────────────────────────────────────────
  useEffect(() => {
    if (externalMuted === undefined) return;
    if (externalMuted === isMutedRef.current) return;
    isExternalUpdateRef.current = true;
    setIsMutedSync(externalMuted);
    applyGain(volumeRef.current, externalMuted);
    isExternalUpdateRef.current = false;
  }, [externalMuted]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── MODE 3 : niveaux lus dans audioLevelStore ─────────────────────────────
  // Les sources hors deck n'ont pas d'entrée dans audioLevelStore (le metering ne
  // couvre que le deck) : on lit donc directement les analyseurs du registry, sans
  // rien y écrire. L'entrée peut apparaître plus tard (acquisition asynchrone).
  useEffect(() => {
    if (!isMeterMode || !meterRegistry || !meterSourceId) return;
    const meter = createMeter(meterType, { dbFloor: MIN_DB, dbCeil: MAX_DB });
    let active = true;
    let raf = 0;
    let dataL: Float32Array | null = null;
    let dataR: Float32Array | null = null;
    const loudBuf: { L?: Float32Array; R?: Float32Array } = {};
    let loudAt    = -Infinity;
    let loudValue: number | null = null;
    const tick = () => {
      if (!active) return;
      const analysers = meterRegistry.getAnalysers(`audio-${meterSourceId}`);
      if (analysers) {
        if (!dataL || dataL.length !== analysers.analyserL.fftSize) {
          dataL = new Float32Array(analysers.analyserL.fftSize);
          dataR = new Float32Array(analysers.analyserR.fftSize);
        }
        analysers.analyserL.getFloatTimeDomainData(dataL);
        analysers.analyserR.getFloatTimeDomainData(dataR!);
        // Le VU suit le fader (et tombe à zéro voie coupée). Pour une source du deck, le
        // fader est DÉJÀ appliqué avant les analyseurs (gainNode du registry, réglé par
        // useAudioMetering) : on ne rejoue que la part absente du signal mesuré, sans quoi
        // le gain comptait deux fois (fader à −6 dB → VU à −12, avant le 2026-10-23).
        const fader      = Math.pow(10, sliderToDB(volumeRef.current) / 20);
        const upstream   = meterRegistry.getGain(`audio-${meterSourceId}`);
        const gainLinear = isMutedRef.current || upstream <= 0 ? 0 : fader / upstream;
        // Mode LUFS : loudness Momentary de la source, la même valeur sur les deux voies.
        const now  = performance.now();
        const lufs = getVuMeterMode() !== 'lufs' ? null
                   : now - loudAt < LUFS_REFRESH_MS ? loudValue
                   : readLufs(meterRegistry, `audio-${meterSourceId}`, gainLinear, loudBuf);
        if (lufs !== null) {
          if (now - loudAt >= LUFS_REFRESH_MS) { loudAt = now; loudValue = lufs; }
          setLevelL(lufs);
          setLevelR(lufs);
        } else {
          const { left, right } = meter.measure(dataL, dataR!, gainLinear);
          setLevelL(left);
          setLevelR(right);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { active = false; cancelAnimationFrame(raf); meter.reset(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeterMode, meterRegistry, meterSourceId]);

  // ── MODE 2 : boucle RAF sur les analyseurs du registry ───────────────────
  useEffect(() => {
    if (!isRegistryMode || !audioSourceRegistry || !audioSourceId) return;

    audioSourceRegistry.resume().catch(() => {});
    acquireMonitor(audioSourceRegistry, audioSourceId);
    audioSourceRegistry.setMonitorGain(audioSourceId, isMutedRef.current ? 0 : 1);
    applyGain(volumeRef.current, isMutedRef.current);

    let   active      = true;
    const meter       = createMeter(meterType, { dbFloor: MIN_DB, dbCeil: MAX_DB });

    // Génération de l'entrée du registry au moment du branchement — cf. le tick.
    let generation = audioSourceRegistry.getGeneration(audioSourceId);

    // ── Les buffers sont alloués dynamiquement au premier tick en lisant
    //    fftSize depuis l'analyseur — évite le mismatch fftSize/buffer ──────
    let dataL: Float32Array | null = null;
    let dataR: Float32Array | null = null;
    const loudBuf: { L?: Float32Array; R?: Float32Array } = {};
    let loudAt    = -Infinity;
    let loudValue: number | null = null;

    const tick = () => {
      if (!active) return;

      // Le flux retour peut être relâché puis réacquis sans que ce composant soit
      // remonté (bouton « Réinitialiser HLS », chaîne désactivée puis réactivée,
      // changement d'URL de monitoring — cf. useMonitorStream). L'entrée du registry
      // est alors recréée avec des nœuds de gain neufs et muets, alors que ce panneau
      // affiche toujours son fader ouvert : le son était perdu jusqu'à ce que
      // l'utilisateur touche le fader. On réapplique donc l'état affiché ici.
      const gen = audioSourceRegistry.getGeneration(audioSourceId);
      if (gen !== generation) {
        generation = gen;
        audioSourceRegistry.resume().catch(() => {});
        applyGain(volumeRef.current, isMutedRef.current);
      }

      const analysers = audioSourceRegistry.getAnalysers(audioSourceId);
      if (analysers) {
        // Allouer ou réallouer si fftSize a changé
        if (!dataL || dataL.length !== analysers.analyserL.fftSize) {
          dataL = new Float32Array(analysers.analyserL.fftSize);
          dataR = new Float32Array(analysers.analyserR.fftSize);
        }

        analysers.analyserL.getFloatTimeDomainData(dataL);
        analysers.analyserR.getFloatTimeDomainData(dataR!);

        // En MODE 2, le gain est appliqué avant les analyseurs dans le registry
        // donc on mesure avec gain=1 (le signal analysé est déjà amplifié)
        // Mode LUFS : loudness Momentary, la même valeur sur les deux voies.
        const now  = performance.now();
        const lufs = getVuMeterMode() !== 'lufs' ? null
                   : now - loudAt < LUFS_REFRESH_MS ? loudValue
                   : readLufs(audioSourceRegistry, audioSourceId, 1, loudBuf);
        if (lufs !== null) {
          if (now - loudAt >= LUFS_REFRESH_MS) { loudAt = now; loudValue = lufs; }
          setLevelL(lufs);
          setLevelR(lufs);
        } else {
          const { left, right } = meter.measure(dataL, dataR!, 1);
          setLevelL(left);
          setLevelR(right);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Ne couper le retour que si plus aucun autre panneau ne l'écoute.
      if (releaseMonitor(audioSourceRegistry, audioSourceId)) {
        audioSourceRegistry.setMonitorGain(audioSourceId, 0);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegistryMode, audioSourceRegistry, audioSourceId]);

  // ── MODE 1 : initialisation Web Audio sur le MediaStream ─────────────────
  useEffect(() => {
    if (isRegistryMode || isMeterMode) return;
    if (!mediaStream)   return;

    const audioTracks = mediaStream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn('[HlsAudioPanel mode1] MediaStream sans track audio');
      return;
    }

    let cancelled = false;

    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current  = null;
      analyserLRef.current = null;
      analyserRRef.current = null;
      gainRef.current      = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    meterRef.current?.reset();
    meterRef.current = null;

    const ctx      = new AudioContext();
    const src      = ctx.createMediaStreamSource(mediaStream);
    const splitter = ctx.createChannelSplitter(2);
    const aL       = ctx.createAnalyser();
    const aR       = ctx.createAnalyser();
    const gain     = ctx.createGain();

    aL.fftSize = 2048;
    aR.fftSize = 2048;

    gain.gain.value = volumeRef.current > 0
      ? Math.pow(10, sliderToDB(volumeRef.current) / 20)
      : 0;
    if (isMutedRef.current) gain.gain.value = 0;

    src.connect(splitter);
    splitter.connect(aL, 0);
    splitter.connect(aR, 1);
    aL.connect(gain);
    aR.connect(gain);
    if (!noOutput) {
      gain.connect(ctx.destination);
    } else {
      // noOutput=true : pas de son aux enceintes, mais on doit quand même connecter
      // gain à un sink pour que le moteur Web Audio rende la chaîne — sans ça,
      // gain est une feuille orpheline et les AnalyserNodes en amont ne reçoivent
      // jamais de données (VU-mètres à zéro).
      gain.connect(ctx.createMediaStreamDestination());
    }

    audioCtxRef.current  = ctx;
    analyserLRef.current = aL;
    analyserRRef.current = aR;
    gainRef.current      = gain;

    meterRef.current = createMeter(meterType, { dbFloor: MIN_DB, dbCeil: MAX_DB });

    ctx.resume().catch(() => {});
    const resume = () => ctx.state === 'suspended' && ctx.resume().catch(() => {});
    document.addEventListener('click', resume, { once: true });

    const dataL = new Float32Array(aL.fftSize);
    const dataR = new Float32Array(aR.fftSize);

    const tick = () => {
      if (cancelled) return;
      aL.getFloatTimeDomainData(dataL);
      aR.getFloatTimeDomainData(dataR);
      const gainLinear = isMutedRef.current
        ? 0
        : Math.pow(10, sliderToDB(volumeRef.current) / 20);
      const { left, right } = meterRef.current!.measure(dataL, dataR, gainLinear);
      setLevelL(left);
      setLevelR(right);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current)      cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close();
      meterRef.current?.reset();
      meterRef.current       = null;
      audioCtxRef.current    = null;
      analyserLRef.current   = null;
      analyserRRef.current   = null;
      gainRef.current        = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaStream, isRegistryMode, isMeterMode]);

  // ── Handlers locaux ───────────────────────────────────────────────────────

  const handleSlider = (val: number) => {
    resumeAudioContext();
    setVolumeSync(val);
    applyGain(val, isMutedRef.current);
    if (!editing) setGainText(fmtDB(sliderToDB(val)));
    if (!isMeterMode && isMutedRef.current && val > 0) {
      setIsMutedSync(false);
      onMutedChange?.(false);
    }
    if (!isExternalUpdateRef.current) onVolumeChange?.(val);
  };

  const handleMute = () => {
    resumeAudioContext();
    const next = !isMutedRef.current;
    setIsMutedSync(next);
    applyGain(volumeRef.current, next);
    if (!isExternalUpdateRef.current) onMutedChange?.(next);
  };

  const handleGainFocus = () => {
    setEditing(true);
    const db = sliderToDB(volumeRef.current);
    setGainText(isFinite(db) ? db.toFixed(1) : '-∞');
  };

  const handleGainBlur = () => {
    setEditing(false);
    const sliderVal = parseGainInput(gainText);
    if (sliderVal !== null) {
      resumeAudioContext();
      setVolumeSync(sliderVal);
      applyGain(sliderVal, isMutedRef.current);
      setGainText(fmtDB(sliderToDB(sliderVal)));
      if (!isExternalUpdateRef.current) onVolumeChange?.(sliderVal);
    } else {
      setGainText(fmtDB(sliderToDB(volumeRef.current)));
    }
  };

  const handleGainKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter')  (e.target as HTMLInputElement).blur();
    if (e.key === 'Escape') { setEditing(false); setGainText(fmtDB(sliderToDB(volumeRef.current))); }
  };

  const atZero         = Math.abs(volume - SLIDER_0DB) < 0.01;
  const SLIDER_TOTAL_W = TRACK_W + HANDLE_OVF * 2;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      gap:           6,
      padding:       '6px 8px 8px',
      background:    'rgba(0,0,0,0.55)',
      borderRadius:  8,
      width:         '100%',
      minWidth:      60,
      flexShrink:    0,
      boxSizing:     'border-box',
      alignSelf:     'stretch',
    }}>

      {isMeterMode && !hideMeterMute && (
        <button
          onClick = {handleMute}
          aria-pressed = {isMuted}
          title   = {isMuted ? 'Voie coupée (le niveau est conservé) — cliquer pour rétablir' : 'Couper cette voie sans toucher au niveau'}
          style   = {{
            width:        '100%',
            height:       26,
            borderRadius: 6,
            cursor:       'pointer',
            fontSize:     11,
            fontWeight:   800,
            fontFamily:   'monospace',
            letterSpacing:'0.5px',
            color:        isMuted ? '#052e0b' : 'rgba(255,255,255,0.8)',
            background:   isMuted ? '#25C400' : 'rgba(255,255,255,0.07)',
            border:       `1px solid ${isMuted ? '#25C400' : 'rgba(255,255,255,0.18)'}`,
            boxShadow:    isMuted ? '0 0 10px rgba(37,196,0,0.7)' : 'none',
            flexShrink:   0,
            transition:   'background 0.15s, box-shadow 0.15s',
          }}
        >
          MUTE
        </button>
      )}

      <div style={{
        display:        'flex',
        gap:            6,
        flex:           '1 1 0',
        minHeight:      0,
        alignItems:     'stretch',
        width:          '100%',
        position:       'relative',
        justifyContent: 'center',
      }}>

        {/* ── Colonne labels ── */}
        <div style={{
          display:       'flex',
          flexDirection: 'column',
          width:         LABEL_COL_W,
          flexShrink:    0,
          position:      'relative',
          zIndex:        2,
        }}>
          <div style={{ height: 10, flexShrink: 0 }} />
          <div style={{ position: 'relative', flex: '1 1 0', minHeight: 0 }}>
            {DB_LABELLED.map(db => {
              const topPct = (1 - dbToFrac(db)) * 100;
              const color  = db > 0 ? '#ef4444' : db === 0 ? '#eab308' : '#ffffff';
              return (
                <span key={db} style={{
                  position:   'absolute',
                  top:        `${topPct.toFixed(2)}%`,
                  left:       0,
                  transform:  'translateY(-50%)',
                  fontSize:   12,
                  fontFamily: 'monospace',
                  color,
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}>
                  {db > 0 ? `+${db}` : db}
                </span>
              );
            })}
          </div>
        </div>

        {/* ── Graduations ── */}
        <div style={{
          position:      'absolute',
          left:          LABEL_COL_W + 6,
          right:         SLIDER_TOTAL_W,
          top:           10,
          bottom:        0,
          pointerEvents: 'none',
          zIndex:        0,
        }}>
          {DB_MARKS.map(db => {
            const topPct = (1 - dbToFrac(db)) * 100;
            const color  = db > 0 ? 'rgba(239,68,68,0.4)'
                         : db === 0 ? 'rgba(234,179,8,0.55)'
                         : 'rgba(255,255,255,0.12)';
            return (
              <div key={db} style={{
                position:   'absolute',
                top:        `${topPct.toFixed(2)}%`,
                left:       0,
                right:      0,
                height:     1,
                background: color,
                transform:  'translateY(-50%)',
              }} />
            );
          })}
        </div>

        <VuBar level={levelL} muted={isMuted} label="L" mode={vuMode} />
        <VuBar level={levelR} muted={isMuted} label="R" mode={vuMode} />

        {/* ── Slider vertical style T-Bar ── */}
        <div style={{
          display:       'flex',
          flexDirection: 'column',
          flex:          `0 0 ${SLIDER_TOTAL_W}px`,
          position:      'relative',
          zIndex:        1,
        }}>
          <div style={{ height: 10, flexShrink: 0 }} />
          <TBarSlider
            value={isMeterMode ? volume : (isMuted ? 0 : volume)}
            onChange={handleSlider}
            atZero={atZero}
          />
        </div>

      </div>

      {/* ── Gain + Mute ── */}
      <div style={{
        display:        'flex',
        flexDirection:  'row',
        alignItems:     'center',
        justifyContent: footerExtra ? 'space-between' : 'center',
        gap:            6,
        width:          '100%',
      }}>
        <input
          type      = "text"
          value     = {gainText}
          onChange  = {e => setGainText(e.target.value)}
          onFocus   = {handleGainFocus}
          onBlur    = {handleGainBlur}
          onKeyDown = {handleGainKey}
          title     = "Gain du fader — taper une valeur en dB pour l'appliquer"
          style     = {{
            width:        footerExtra ? 52 : 64,
            minWidth:     0,
            textAlign:    'center',
            fontSize:     11,
            fontFamily:   'monospace',
            background:   editing ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)',
            border:       `1px solid ${editing ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.13)'}`,
            borderRadius: 4,
            color:        '#ffffff',
            padding:      '2px 3px',
            cursor:       'text',
            outline:      'none',
            transition:   'background 0.15s, border-color 0.15s',
          }}
        />
        {!isMeterMode && (
        <button
          onClick = {handleMute}
          title   = {isMuted ? 'Réactiver' : 'Mute'}
          style   = {{
            background:     isMuted ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.07)',
            border:         `1px solid ${isMuted ? '#ef4444' : 'rgba(255,255,255,0.13)'}`,
            borderRadius:   6,
            cursor:         'pointer',
            color:          isMuted ? '#ef4444' : 'rgba(255,255,255,0.65)',
            padding:        '3px 5px',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            width:          28,
            height:         24,
            flexShrink:     0,
            transition:     'background 0.15s, border-color 0.15s, color 0.15s',
          }}
        >
          {isMuted ? <IconMuted /> : <IconSound />}
        </button>
        )}
        {footerExtra}
      </div>

    </div>
  );
};

export default HlsAudioPanel;